import { gql, useQuery } from "@apollo/client";
import React from "react";
import { useRouteMatch } from "react-router";
import { BreadCrumb, BreadCrumbProp } from "../../../../components/light/Breadcrumb";
import { Content } from "../../../../components/light/Content";
import { SectionBlock } from "../../../../components/light/SectionBlock";
import { Loading } from "../../../../components/Loading";
import moment from "moment";
import { Graph } from "src/generated/graph";
import { IndexTable, LegacyCard } from "@shopify/polaris";
import { useTranslation } from "react-i18next";

const QUERY = gql`
  query merchantShipmentItemHistory($id: Int!) {
    merchantShipmentItemHistory(itemId: $id)
  }
`;

interface Props {
  called?: boolean;
}

export function ProductShipmentItemHistoryScreen(props: Props) {
  const { t } = useTranslation();
  const { params }: any = useRouteMatch();
  const { data, loading } = useQuery<Graph.Query>(QUERY, {
    fetchPolicy: "no-cache",
    variables: {
      id: Number(params.id),
    },
  });

  const BreadCrumbItems: BreadCrumbProp[] = [
    {
      text: "Applications",
      to: "#",
    },
    {
      text: "Products",
      to: "#",
    },
    {
      text: "List",
      to: "/products/items",
    },
    {
      text: "Shipment History",
      to: "/product/shipment/" + params.id,
    },
  ];

  if (data === undefined || loading) {
    return (
      <Content breadcrumb={props.called ? null : <BreadCrumb items={BreadCrumbItems} />}>
        <div className="row">
          <div className="col-md-7">
            <SectionBlock title={"Shipment History of Item #" + params.id} transparent={true}>
              <Loading />
            </SectionBlock>
          </div>
        </div>
      </Content>
    );
  }

  return (
    <Content breadcrumb={props.called ? null : <BreadCrumb items={BreadCrumbItems} />}>
      <div className="row">
        <div className="col-md-5">
          {props.called && (
            <LegacyCard>
              <LegacyCard.Section>
                <b>{`${t('shipment_history_of_item')} #${params.id}`}</b>
              </LegacyCard.Section>
              <LegacyCard.Section flush>
                <IndexTable
                  headings={[
                    { title: t('sku') },
                    { title: t('created_date') },
                    { title: t('qty'), alignment: "center" },
                    { title: t('received'), alignment: "center" },
                    { title: t("shipper_received"), alignment: "center" },
                  ]}
                  loading={loading}
                  itemCount={data.merchantShipmentItemHistory.length || 0}
                  selectable={false}
                >
                  {data.merchantShipmentItemHistory.map((item: any, i: number) => {
                    return (
                      <IndexTable.Row key={i} position={i} id={i + ""}>
                        <IndexTable.Cell>
                          <>
                            <small>
                              <strong>#{item.sku_id}</strong>
                            </small>
                            <br />
                            <small style={{ textTransform: "capitalize" }}>{t('color')}: {item.color}</small>
                            <br />
                            <small style={{ textTransform: "capitalize" }}>{t('size')}: {item.size}</small>
                            <br />
                            <small className="badge bg-info text-dark">
                              <strong style={{ textTransform: "capitalize" }}>{t(item.status.toLowerCase())}</strong>
                            </small>
                          </>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {moment(new Date(item.inputdate)).format("YYYY:MM:DD HH:mm:ss")}
                        </IndexTable.Cell>
                        <IndexTable.Cell className="text-center">{item.qty}</IndexTable.Cell>
                        <IndexTable.Cell className="text-center">{item.received || 0}</IndexTable.Cell>
                        <IndexTable.Cell className="text-center">{item.shipper_received || 0}</IndexTable.Cell>
                      </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              </LegacyCard.Section>
            </LegacyCard>
          )}
          {!props.called && (
            <SectionBlock title={"Shipment History of Item #" + params.id} transparent={true}>
              <table className="table table-stripped">
                <thead>
                  <tr>
                    <th>Sku</th>
                    <th>Created Date</th>
                    <th className="text-center">QTY</th>
                    <th className="text-center">Received</th>
                    <th className="text-center">Shipper Received</th>
                  </tr>
                </thead>
                <tbody>
                  {data.merchantShipmentItemHistory.map((item: any) => {
                    return (
                      <tr key={item.id}>
                        <td>
                          <small>
                            <strong>#{item.sku_id}</strong>
                          </small>
                          <br />
                          <small style={{ textTransform: "capitalize" }}>color: {item.color}</small>
                          <br />
                          <small style={{ textTransform: "capitalize" }}>size: {item.size}</small>
                          <br />
                          <small className="badge bg-info text-dark">
                            <strong style={{ textTransform: "capitalize" }}>{item.status.toLowerCase()}</strong>
                          </small>
                        </td>
                        <td>{moment(new Date(item.inputdate)).format("YYYY:MM:DD HH:mm:ss")}</td>
                        <td className="text-center">{item.qty}</td>
                        <td className="text-center">{item.received || 0}</td>
                        <td className="text-center">{item.shipper_received || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SectionBlock>
          )}
        </div>
      </div>
    </Content>
  );
}
